// frontend/src/pages/LocalCompraDetailPage/LocalCompraDetailPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Layout, Typography, Button, Card, List, Spin, Alert, App, Input, Tag, Space, Empty, Modal,
} from 'antd';
import {
  PlusOutlined, ShopOutlined, EditOutlined, InboxOutlined, RightOutlined, UndoOutlined,
  SearchOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getLocalCompra, getListasCompra, arquivarLocalCompra, reativarLocalCompra,
} from '../../services/comprasService';
import { getCategorias } from '../../services/financeService';
import { getErrorMessage } from '../../utils/errorHandler';
import LocalCompraModal, { nomeEhGenerico } from '../../components/LocalCompraModal/LocalCompraModal';
import NovaListaModal from '../../components/NovaListaModal/NovaListaModal';

const { Content } = Layout;
const { Title, Text } = Typography;

const brl = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const fmtData = (d) => (d ? dayjs(String(d).slice(0, 10)).format('DD/MM/YYYY') : '—');

const STATUS_TAG = {
  RASCUNHO: { color: 'default', label: 'Rascunho' },
  EM_COMPRA: { color: 'processing', label: 'Em compra' },
  CONCLUIDA: { color: 'gold', label: 'Concluída' },
  LANCADA: { color: 'green', label: 'Lançada' },
  CANCELADA: { color: 'red', label: 'Cancelada' },
};

const ABERTAS_STATUS = ['RASCUNHO', 'EM_COMPRA'];

function LocalCompraDetailPage() {
  const { id } = useParams();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [modal, modalCtx] = Modal.useModal();

  const [local, setLocal] = useState(null);
  const [listas, setListas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busca, setBusca] = useState('');
  const [mostrarFeitas, setMostrarFeitas] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [listaModalOpen, setListaModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [loc, lst] = await Promise.all([
        getLocalCompra(id),
        getListasCompra({ local: id }),
      ]);
      setLocal(loc);
      setListas(lst.filter((l) => !l.eh_modelo));
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível carregar este local.'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    getCategorias().then((d) => setCategorias(Array.isArray(d) ? d : d?.results || [])).catch(() => {});
  }, [fetchData]);

  const listasVisiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const base = termo
      ? listas.filter((l) => (l.titulo || '').toLowerCase().includes(termo))
      : listas;
    return {
      abertas: base.filter((l) => ABERTAS_STATUS.includes(l.status)),
      feitas: base.filter((l) => !ABERTAS_STATUS.includes(l.status)),
    };
  }, [listas, busca]);

  const handleArquivar = () => {
    modal.confirm({
      title: `Arquivar "${local.nome}"?`,
      content: 'As listas do local continuam acessíveis. Você pode reativá-lo depois em "Exibir arquivados".',
      okText: 'Arquivar',
      okType: 'danger',
      onOk: async () => {
        try {
          await arquivarLocalCompra(local.id);
          message.success('Local arquivado.');
          navigate('/listas-compras');
        } catch (err) {
          message.error(getErrorMessage(err, 'Erro ao arquivar.'));
        }
      },
    });
  };

  const handleReativar = async () => {
    try {
      await reativarLocalCompra(local.id);
      message.success('Local reativado.');
      fetchData();
    } catch (err) {
      message.error(getErrorMessage(err, 'Erro ao reativar.'));
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center' }}>
        <Spin size="large" tip="Carregando..." />
      </div>
    );
  }

  if (error || !local) {
    return (
      <Content style={{ padding: 'clamp(1rem, 4vw, 2rem)' }}>
        <Alert type="error" showIcon message={error || 'Local não encontrado.'} />
        <Button style={{ marginTop: 16 }} onClick={() => navigate('/listas-compras')}>
          Voltar
        </Button>
      </Content>
    );
  }

  const renderItem = (lista) => {
    const st = STATUS_TAG[lista.status] || STATUS_TAG.RASCUNHO;
    const totalMostrar = Number(lista.total_final) > 0 ? lista.total_final : lista.total_estimado;
    return (
      <List.Item
        style={{ padding: '14px 20px', cursor: 'pointer' }}
        onClick={() => navigate(`/listas-compras/${lista.id}`)}
        actions={[<RightOutlined key="go" style={{ color: '#bbb' }} />]}
      >
        <List.Item.Meta
          title={
            <Space>
              {lista.titulo || 'Lista'}
              <Tag color={st.color}>{st.label}</Tag>
            </Space>
          }
          description={
            <Text type="secondary" style={{ fontSize: 12 }}>
              {fmtData(lista.data_compra)} · {lista.total_itens} item(ns)
            </Text>
          }
        />
        <Text strong>{brl(totalMostrar)}</Text>
      </List.Item>
    );
  };

  return (
    <Content style={{ padding: 'clamp(1rem, 4vw, 2rem)', width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
      {modalCtx}
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/listas-compras')}
        style={{ paddingLeft: 0, marginBottom: 8 }}
      >
        Todos os locais
      </Button>

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0, flex: '1 1 260px' }}>
            <ShopOutlined style={{ fontSize: 32, color: '#50B0A0', marginTop: 4 }} />
            <div style={{ minWidth: 0 }}>
              <Title level={3} style={{ margin: 0, wordBreak: 'break-word' }}>{local.nome}</Title>
              <Text type="secondary">{local.categoria_nome || 'Sem categoria padrão'}</Text>
              {(local.eh_generico ?? nomeEhGenerico(local.nome)) && (
                <div style={{ marginTop: 8 }}>
                  <Tag color="warning">Nome genérico — use "Organizar" na listagem para separar por loja</Tag>
                </div>
              )}
            </div>
          </div>
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={() => setEditModalOpen(true)}>Editar</Button>
            {local.status === 'ARQUIVADO' ? (
              <Button icon={<UndoOutlined />} onClick={handleReativar}>Reativar</Button>
            ) : (
              <Button icon={<InboxOutlined />} onClick={handleArquivar}>Arquivar</Button>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setListaModalOpen(true)}>
              Nova lista aqui
            </Button>
          </Space>
        </div>
      </Card>

      <Card
        title="Listas"
        styles={{ body: { padding: 0 } }}
      >
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            placeholder="Buscar lista"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ maxWidth: 320 }}
          />
        </div>

        <div style={{ padding: '8px 20px', fontSize: 12, fontWeight: 600, color: '#8c8c8c', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
          Em aberto ({listasVisiveis.abertas.length})
        </div>
        {listasVisiveis.abertas.length > 0 ? (
          <List dataSource={listasVisiveis.abertas} renderItem={renderItem} />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={busca.trim() ? 'Nenhuma lista em aberto para esta busca' : 'Nenhuma lista em aberto'}
            style={{ padding: '24px 0' }}
          >
            {!busca.trim() && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setListaModalOpen(true)}>
                Nova lista
              </Button>
            )}
          </Empty>
        )}

        {listasVisiveis.feitas.length > 0 && (
          <>
            <div
              style={{
                padding: '8px 20px', fontSize: 12, fontWeight: 600, color: '#8c8c8c', background: '#fafafa',
                borderBottom: '1px solid #f0f0f0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              }}
              onClick={() => setMostrarFeitas((v) => !v)}
            >
              <RightOutlined style={{ fontSize: 10, transform: mostrarFeitas ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
              Concluídas e lançadas ({listasVisiveis.feitas.length})
            </div>
            {mostrarFeitas && <List dataSource={listasVisiveis.feitas} renderItem={renderItem} />}
          </>
        )}
      </Card>

      <LocalCompraModal
        open={editModalOpen}
        local={local}
        categorias={categorias}
        onClose={() => setEditModalOpen(false)}
        onSaved={(atualizado) => { setEditModalOpen(false); setLocal(atualizado); }}
      />

      <NovaListaModal
        open={listaModalOpen}
        locais={[local]}
        localPadrao={local.id}
        onClose={() => setListaModalOpen(false)}
        onCreated={(nova) => { setListaModalOpen(false); navigate(`/listas-compras/${nova.id}`); }}
      />
    </Content>
  );
}

export default LocalCompraDetailPage;
